import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { levelToSeverity, SeverityIcon } from "../../components/primitives";
import type { EventLevel } from "../../types";
import { EVENT_LEVELS, sourceLabel } from "./utils";

/** Level multi-toggle (error / warning / info); at least one level stays on. */
export function LevelToggle({
  value,
  onChange,
  label,
  className,
}: {
  value: EventLevel[];
  onChange: (levels: EventLevel[]) => void;
  label: string;
  className?: string;
}) {
  const { t } = useLocalization();
  return (
    <ToggleGroup
      type="multiple"
      size="sm"
      variant="outline"
      value={value}
      onValueChange={(next) => {
        if (next.length) onChange(EVENT_LEVELS.filter((level) => next.includes(level)));
      }}
      aria-label={label}
      className={cn("flex-wrap justify-start gap-0.5", className)}
    >
      {EVENT_LEVELS.map((level) => (
        <ToggleGroupItem key={level} value={level} className="h-8 gap-1.5 px-2 text-xs">
          <SeverityIcon severity={levelToSeverity(level)} className="h-3.5 w-3.5" />
          {t(`monitoring.level.${level}`)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

/** "Camera  capture_trigger_failed": the source in words, the code as a mono chip. */
export function SourceCode({ source, code, className }: { source: string; code: string; className?: string }) {
  return (
    <span className={cn("inline-flex min-w-0 flex-wrap items-center gap-1.5", className)}>
      <span className="text-xs text-muted-foreground">{sourceLabel(source)}</span>
      <code className="break-all rounded border border-border bg-muted/40 px-1 py-px font-mono text-[10px] text-foreground/90">
        {code}
      </code>
    </span>
  );
}

/** Copies a text, with a short confirmation on the button and a toast. */
export function CopyButton({ text, what, className }: { text: string; what: string; className?: string }) {
  const { t } = useLocalization();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t("common.copied", { what }));
    } catch {
      toast.error(t("common.copyFailed", { what }));
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn("h-7 gap-1 px-2 text-xs", className)}
      onClick={() => void copy()}
      aria-label={t("monitoring.events.copy", { what })}
    >
      {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
      {t("common.copy")}
    </Button>
  );
}
